// Find & Replace for the article Tiptap editor. Implemented as a ProseMirror
// plugin that decorates matches inline and exposes an imperative command
// surface via plugin metadata. Keeps zero external deps — everything lives
// in the ProseMirror packages already pulled in by Tiptap.

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export const searchAndReplaceKey = new PluginKey("searchAndReplace");

// Escape user input for use inside a RegExp — treats every character
// literally rather than interpreting `.`, `*`, `(`, etc.
const escapeRegex = (s) => String(s || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Walk the doc collecting `{from, to}` for every occurrence of `query`.
// Matches are computed per text-node so highlights don't span across
// non-text boundaries (which ProseMirror would refuse to render anyway).
function buildMatches(doc, query, caseSensitive) {
    if (!query) return [];
    const flags = caseSensitive ? "g" : "gi";
    const re = new RegExp(escapeRegex(query), flags);
    const matches = [];
    doc.descendants((node, pos) => {
        if (!node.isText) return true;
        const text = node.text || "";
        let m;
        while ((m = re.exec(text)) !== null) {
            const from = pos + m.index;
            const to = from + m[0].length;
            matches.push({ from, to });
            // Guard against zero-width matches locking us in a loop.
            if (m.index === re.lastIndex) re.lastIndex++;
        }
        return false; // don't recurse into text nodes.
    });
    return matches;
}

function buildDecorations(matches, activeIdx) {
    if (!matches.length) return DecorationSet.empty;
    const decos = matches.map((m, i) =>
        Decoration.inline(m.from, m.to, {
            class:
                i === activeIdx
                    ? "pm-search-match pm-search-match-active"
                    : "pm-search-match",
        }),
    );
    return DecorationSet.create(matches._doc || null, decos);
}

const emptyState = {
    query: "",
    replaceQuery: "",
    caseSensitive: false,
    matches: [],
    activeIdx: 0,
};

// Rebuild derived fields (matches + decorations) from the doc + query.
function rebuild(doc, next) {
    const matches = buildMatches(doc, next.query, next.caseSensitive);
    const activeIdx = matches.length
        ? Math.min(Math.max(next.activeIdx || 0, 0), matches.length - 1)
        : 0;
    const decorations = matches.length
        ? DecorationSet.create(
            doc,
            matches.map((m, i) =>
                Decoration.inline(m.from, m.to, {
                    class:
                        i === activeIdx
                            ? "pm-search-match pm-search-match-active"
                            : "pm-search-match",
                }),
            ),
        )
        : DecorationSet.empty;
    return { ...next, matches, activeIdx, decorations };
}

export const SearchAndReplace = Extension.create({
    name: "searchAndReplace",

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: searchAndReplaceKey,
                state: {
                    init: (_config, state) => ({
                        ...emptyState,
                        decorations: DecorationSet.empty,
                        _doc: state.doc,
                    }),
                    apply(tr, prev, _oldState, newState) {
                        const meta = tr.getMeta(searchAndReplaceKey);
                        // Doc changed but no meta — remap decorations forward.
                        if (!meta) {
                            if (tr.docChanged) {
                                // If a query is active, recompute matches so
                                // replace-all and typing keep highlights fresh.
                                if (prev.query) {
                                    return rebuild(newState.doc, {
                                        query: prev.query,
                                        replaceQuery: prev.replaceQuery,
                                        caseSensitive: prev.caseSensitive,
                                        activeIdx: prev.activeIdx,
                                    });
                                }
                                return {
                                    ...prev,
                                    decorations: prev.decorations.map(tr.mapping, tr.doc),
                                };
                            }
                            return prev;
                        }
                        if (meta.type === "clear") {
                            return {
                                ...emptyState,
                                decorations: DecorationSet.empty,
                            };
                        }
                        if (meta.type === "set") {
                            return rebuild(newState.doc, {
                                query: meta.query ?? prev.query,
                                replaceQuery:
                                    meta.replaceQuery ?? prev.replaceQuery,
                                caseSensitive:
                                    meta.caseSensitive ?? prev.caseSensitive,
                                activeIdx: meta.resetActive ? 0 : prev.activeIdx,
                            });
                        }
                        if (meta.type === "step" && prev.matches.length) {
                            const next =
                                (prev.activeIdx + meta.direction + prev.matches.length)
                                % prev.matches.length;
                            return rebuild(newState.doc, {
                                query: prev.query,
                                replaceQuery: prev.replaceQuery,
                                caseSensitive: prev.caseSensitive,
                                activeIdx: next,
                            });
                        }
                        return prev;
                    },
                },
                props: {
                    decorations(state) {
                        return this.getState(state).decorations;
                    },
                },
            }),
        ];
    },
});

// -------- imperative helpers used by the toolbar --------

export function setSearchQuery(editor, opts) {
    editor.view.dispatch(
        editor.view.state.tr.setMeta(searchAndReplaceKey, {
            type: "set",
            resetActive: true,
            ...opts,
        }),
    );
}

export function clearSearch(editor) {
    editor.view.dispatch(
        editor.view.state.tr.setMeta(searchAndReplaceKey, { type: "clear" }),
    );
}

export function stepMatch(editor, direction) {
    editor.view.dispatch(
        editor.view.state.tr.setMeta(searchAndReplaceKey, {
            type: "step",
            direction,
        }),
    );
    // Scroll the active match into view after the state settles.
    requestAnimationFrame(() => {
        const el = editor.view.dom.querySelector(".pm-search-match-active");
        if (el && el.scrollIntoView)
            el.scrollIntoView({ block: "center", behavior: "smooth" });
    });
}

export function getSearchState(editor) {
    return searchAndReplaceKey.getState(editor.view.state) || emptyState;
}

export function replaceCurrent(editor) {
    const s = getSearchState(editor);
    if (!s.matches.length) return;
    const m = s.matches[s.activeIdx];
    if (!m) return;
    editor
        .chain()
        .focus()
        .insertContentAt({ from: m.from, to: m.to }, s.replaceQuery || "")
        .run();
}

export function replaceAll(editor) {
    const s = getSearchState(editor);
    if (!s.matches.length) return 0;
    // Replace from the end so earlier positions stay valid.
    const sorted = [...s.matches].sort((a, b) => b.from - a.from);
    const { tr } = editor.view.state;
    for (const m of sorted) {
        tr.insertText(s.replaceQuery || "", m.from, m.to);
    }
    editor.view.dispatch(tr);
    return sorted.length;
}
